import classNames from 'classnames'
import React from 'react'

import { Heading } from '../../components/typography'
import { useStickyShadow } from '../../hooks/use-sticky-shadow'
import { shortFormatNumber } from '../../util'

interface Props {
  domainElement: React.ReactElement | string
  sector?: string | null
  subSector?: string | null
  monthlyVisits: Array<number | null>
}

export default function TrafficHeader({ domainElement, sector, subSector, monthlyVisits }: Props) {
  const { stickyRef, isSticky } = useStickyShadow()

  const headerColumns = React.useMemo(() => {
    const lastMonthVisits = monthlyVisits[monthlyVisits.length - 1]
    const prevMonthVisits = monthlyVisits[monthlyVisits.length - 2]
    const visitsChangePercentage =
      typeof lastMonthVisits === 'number' && typeof prevMonthVisits === 'number'
        ? ((lastMonthVisits - prevMonthVisits) / Math.abs(prevMonthVisits)) * 100
        : null

    return [
      { label: 'Domain', value: domainElement },
      {
        label: 'Last Month Visits',
        value: `${shortFormatNumber(lastMonthVisits, { fractions: 2 })} ${visitsChangePercentage !== null ? `(${shortFormatNumber(visitsChangePercentage, { fractions: 2, showPlusSign: true })}%)` : ''}`,
      },
      // Used || intentionally in case sector/subSection is '' (empty string)
      { label: 'Sector', value: sector || '—' },
      { label: 'Sub Sector', value: subSector || '—' },
    ].map(({ label, value }, index) => (
      <div className="flex flex-1 flex-col" key={`${label}-${value}`}>
        {index === 0 && React.isValidElement(value) ? (
          value
        ) : (
          <Heading level={3} className="">
            {value}
          </Heading>
        )}
        <span className="text-2xs leading-4 text-muted">{label}</span>
      </div>
    ))
  }, [domainElement, monthlyVisits, sector, subSector])

  return (
    <div className="sticky z-30 bg-primary py-2.5 sticky-22 dark:border-gray-700" ref={stickyRef}>
      <div
        className={classNames('pointer-events-none absolute left-0 top-0 h-full w-full shadow-md', {
          'opacity-100 transition-opacity duration-200': isSticky,
          'opacity-0': !isSticky,
        })}
      />
      <div className="fv-container flex items-center justify-between px-2.5 xl:px-5">
        <div className="flex w-full justify-between">{headerColumns}</div>
      </div>
    </div>
  )
}
